import { apiGet } from '@/lib/api';
import { applyDocumentTextEdits, type DocumentTextEdit } from '@/components/ai/document-text-edits';
// ============================================
// document-view.tsx — R14/R15/R20 Document mode root component
// ============================================
//
// Yjs-connected TipTap editor + auto-save. Mounted in
// `project-detail.tsx` when `viewType === 'DOCUMENT'`.
//
// R15: the in-editor tab strip (`DocumentTabs`) was removed. The
// saved views sidebar already shows every DOCUMENT view + a
// "General" entry (replacing the old "All tasks" default), so the
// duplicate tab UI inside the document area was confusing —
// clicking a tab in the editor did nothing the sidebar couldn't
// do.
//
// R20 — CRITICAL FIX (Bug 1, "per-view content"):
// R14–R19 had `activeViewId` as INTERNAL state on DocumentView
// that was auto-initialised to the first DOCUMENT view but never
// updated when the user clicked a different view in the saved-
// views sidebar (the sidebar's `onViewSelect` only updated the
// OUTER `activeViewId` in `project-detail.tsx`, which DocumentView
// never received). Result: DocumentView always rendered the first
// view regardless of the sidebar selection — every view showed the
// same content + sync.
//
// The component now receives `activeView` as a PROP from the parent
// (mirroring Kanban/Calendar/Gantt). The parent already derives
// the SavedView object from its outer `activeViewId` state, so
// DocumentView just consumes it. The internal `activeViewId` state
// is gone — the prop IS the source of truth. Switching views in
// the sidebar now propagates immediately to the document editor.
//
// We also no longer need the "auto-select first view" effect: the
// parent always passes a valid `activeView` (null = General tab).
// When `activeView` flips, the new Y.Doc + WebsocketProvider +
// snapshot fetch all start fresh — Yjs CRDTs are cheap to create
// from scratch and this keeps per-view state isolated.
//
// R24 (Bug — "General" entry in the sidebar): the synthetic
// `activeViewId === null` scratchpad is GONE from the UI. Document
// mode now always opens a real DOCUMENT saved view (selected by the
// parent's auto-select effect); with zero DOCUMENT views this
// component renders its "create your first document" empty state and
// the CTA selects the freshly created view via `onSelectView`. While
// the parent's selection is still landing we render a neutral
// placeholder instead of a local-only editor the user could type
// into and silently lose.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Loader2, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/lib/i18n';

import type { SavedView, ViewConfig } from '@/lib/schemas';
import {
    useCreateSavedView,
} from '@/hooks/use-views';
import {
    useDocumentViews,
    useDocumentSnapshot,
    useSaveDocumentSnapshot,
} from '@/hooks/use-document';
import { useYjsConnection } from '@/hooks/use-yjs-connection';
import { useTasks } from '@/hooks/use-tasks';
import { useProjectMembers } from '@/hooks/use-projects';

import { DocumentToolbar } from './document-toolbar';
import { DocumentEditor, getDocumentEditor } from './document-editor';
import {
    DocumentPersonMentionPopover,
    type DocumentPersonMentionTarget,
} from './document-person-mention-popover';
import { useDocumentI18n } from './document-i18n';
import { Button } from '@/components/ui/button';

// ============================================
// TYPES
// ============================================

/**
 * R26 — honest auto-save state, owned by DocumentView and rendered by
 * the toolbar:
 *
 *   - `saved`  : nothing dirty, no request in flight (the last request
 *                either succeeded or there never was one);
 *   - `dirty`  : unsaved bytes exist but no request is in flight — the
 *                idle debounce / max-wait deadline is still counting;
 *   - `saving` : a POST is in flight right now;
 *   - `error`  : the last POST failed; the bytes are still dirty and
 *                will be retried (next edit, backoff, or hide/blur).
 */
export type DocumentSaveStatus = 'saved' | 'dirty' | 'saving' | 'error';

export interface DocumentViewProps {
    onAskAi?: (viewId: string, name: string) => void;
    aiProposal?: Record<string, unknown> | null;
    onDismissAiProposal?: () => void;
    projectId: string;
    /**
     * The active DOCUMENT view (or null for the synthetic "General"
     * tab). The parent (project-detail.tsx) derives this from its
     * outer `activeViewId` state + the live `useSavedViews` data.
     * DocumentView consumes the prop directly — it no longer
     * maintains its own internal copy (R20 Bug 1 fix).
     */
    activeView?: import('@/lib/schemas').SavedView | null;
    /**
     * The "default" General tab uses this id to scope its data.
     * We don't have a real view row for it (the backend doesn't
     * auto-seed), so we synthesise one with id `__general__` and
     * persist snapshots under that key.
     *
     * For R14 we keep the General tab LOCAL-only — no server
     * persistence — so users get a free scratchpad without creating
     * a view row. The Tabs UI always shows it as the leftmost tab.
     */
    activeUser: { id?: string; name: string };
    canEdit: boolean;
    /**
     * R24: ask the parent (project-detail.tsx) to make a saved view
     * the active one. The parent owns `activeViewId`; DocumentView
     * only uses this to open the document it just created from the
     * empty-state CTA (there is no local selection state anymore).
     */
    onSelectView?: (viewId: string) => void;
    /**
     * R14: called when the user clicks a mention chip in the
     * document. The parent (project-detail.tsx) is responsible for
     * opening the task-detail side panel. DocumentView never imports
     * the side panel directly so the editor stays decoupled from
     * navigation concerns.
     */
    onOpenTask?: (taskId: string) => void;
}

const GENERAL_ROOM = '__general__';

// ============================================
// AUTOSAVE SCHEDULER (pure — no DOM, no React)
// ============================================
//
// R26 — "Saving… forever / only saves on Ctrl+S" fix.
//
// The previous implementation kept a single `debounceTimerRef` inside
// the component and reset it on every keystroke. Two things defeated
// it:
//
//   1. `flushPendingSnapshot` changed identity every render (it
//      depended on the `useMutation()` result object, which is a new
//      object each render). The unmount/tab-switch effect therefore
//      re-ran on EVERY render, and its cleanup cancelled the pending
//      timer — so the debounce never survived more than one render and
//      every keystroke produced an immediate POST of the FULL Yjs
//      document. For a 568 KB document that is ~0.5 MB/s of uploads and
//      a "Saving…" badge that never clears.
//   2. Nothing coalesced requests: while one POST was in flight the
//      next keystroke started another one.
//
// This scheduler owns the whole policy in one place:
//
//   • idle debounce   — flush AUTOSAVE_DEBOUNCE_MS (1 s) after the last
//                       change;
//   • max wait        — never let a continuous typing burst go unsaved
//                       for more than AUTOSAVE_MAX_WAIT_MS (10 s). The
//                       deadline is ABSOLUTE: it is measured from the
//                       first unsaved change and is not pushed back by
//                       later keystrokes, so the "no pause for a full
//                       second" case still flushes;
//   • single flight   — `beginRequest()` refuses a second concurrent
//                       upload; the newest bytes stay pending and
//                       `settleRequest()` tells the caller to flush them
//                       right after the current request completes;
//   • retry backoff   — a failed request keeps the edits dirty, retries
//                       once after the idle interval, and a new change
//                       still flushes immediately.
//
// Every decision is a pure function of explicit clock values
// (`nextFlushDelayMs`, `createAutosaveScheduler` + injected timers), so
// the policy is probe-able in plain Node with fake timers — see the R26
// report for the `/tmp` probe script.

/** Idle debounce: flush this long after the user stops typing. */
export const AUTOSAVE_DEBOUNCE_MS = 1000;
/**
 * Max wait: force a flush at most this long after the FIRST unsaved
 * change, even when the user keeps typing without a 1 s pause.
 */
export const AUTOSAVE_MAX_WAIT_MS = 10000;
/** Delay before retrying a failed upload (a new change beats it). */
export const AUTOSAVE_RETRY_MS = AUTOSAVE_DEBOUNCE_MS;

export interface AutosaveTimers {
    setTimeout: (handler: () => void, delayMs: number) => number;
    clearTimeout: (handle: number) => void;
}

export type AutosaveFlushReason = 'idle' | 'max-wait' | 'retry';

/** Minimal timer surface used by the scheduler (`window.setTimeout` aliases). */
const DEFAULT_AUTOSAVE_TIMERS: AutosaveTimers = {
    setTimeout: (handler, delayMs) => setTimeout(handler, delayMs) as unknown as number,
    clearTimeout: (handle) => clearTimeout(handle),
};

/**
 * Pure scheduling policy — returns the delay (ms) to wait before the
 * next flush:
 *
 *   - `-1` when there is nothing to flush (no change recorded);
 *   - `0`  when a flush is already due (the caller flushes immediately);
 *   - `min(idleRemaining, maxWaitRemaining)` otherwise, where
 *     `idleRemaining = idleMs - (now - lastChangeAt)` and
 *     `maxWaitRemaining = maxWaitMs - (now - firstDirtyAt)`, both
 *     clamped at 0. Because the max-wait deadline is absolute, a
 *     continuous typing burst can be forced to flush with a delay
 *     shorter than the idle debounce.
 */
export function nextFlushDelayMs(params: {
    now: number;
    /** Timestamp of the first still-unsaved change (null = nothing dirty). */
    firstDirtyAt: number | null;
    /** Timestamp of the most recent change (null = no changes yet). */
    lastChangeAt: number | null;
    idleMs?: number;
    maxWaitMs?: number;
}): number {
    const {
        now,
        firstDirtyAt,
        lastChangeAt,
        idleMs = AUTOSAVE_DEBOUNCE_MS,
        maxWaitMs = AUTOSAVE_MAX_WAIT_MS,
    } = params;
    if (firstDirtyAt === null || lastChangeAt === null) return -1;
    const idleRemaining = Math.max(0, idleMs - (now - lastChangeAt));
    const maxWaitRemaining = Math.max(0, maxWaitMs - (now - firstDirtyAt));
    return Math.min(idleRemaining, maxWaitRemaining);
}

export interface AutosaveScheduler {
    /** Bytes waiting to be uploaded (null = nothing pending). */
    readonly pending: Uint8Array | null;
    /** `true` while an upload is in flight. */
    readonly inFlight: boolean;
    /** `true` when the last settled request failed and edits are still dirty. */
    readonly failed: boolean;
    /** Total uploads started (probe/diagnostic counter). */
    readonly requestCount: number;
    /** Record a change; (re)arms the idle + max-wait deadline. */
    noteChange(bytes: Uint8Array, now: number): void;
    /**
     * Try to start a request. Returns the PAYLOAD TO UPLOAD, or `null`
     * when the request must not start (one is already in flight, or
     * nothing is pending).
     *
     * R26 repair: this used to return a boolean and move `pending` into
     * `inFlight`. The caller then read `scheduler.pending` for the bytes
     * — which `beginRequest` had just nulled — so every flush aborted
     * silently and `inFlight` stayed set forever, leaving the document
     * permanently "dirty" and never saved. Returning the payload makes
     * that mistake impossible.
     */
    beginRequest(now: number): Uint8Array | null;
    /** Request settled: true = the caller must flush `pending` right away. */
    settleRequest(ok: boolean, now: number): boolean;
    /** Drop the scheduled timers but keep `pending` (unmount / page hide). */
    cancelTimers(): void;
    /** Back to the initial state (view switch / new document). */
    reset(): void;
}

/**
 * State machine for the autosave policy.
 *
 * `schedule` is injected so the policy stays pure: the component wires
 * it to `window.setTimeout` and forwards the pure `nextFlushDelayMs`
 * decision, while the probe wires it to a virtual clock. `timers` is
 * injectable for the same reason.
 *
 * `beginRequest()` REFUSES to start while another request is in flight,
 * which is what bounds a typing burst to a single upload: subsequent
 * `flush()` calls just mark the newer bytes as pending, and
 * `settleRequest()` returns `true` to request exactly one follow-up
 * flush when those bytes arrived mid-flight.
 */
export function createAutosaveScheduler(
    schedule: (policy: {
        idleMs: number;
        maxWaitMs: number;
        /** Pure policy helper, pre-wired with the policy's own limits. */
        delayMs: (now: number, firstDirtyAt: number | null, lastChangeAt: number | null) => number;
    }) => (reason: AutosaveFlushReason, delayMs: number) => void,
    timers: AutosaveTimers = DEFAULT_AUTOSAVE_TIMERS,
    options?: { idleMs?: number; maxWaitMs?: number; retryMs?: number }
): AutosaveScheduler {
    const policy = {
        idleMs: options?.idleMs ?? AUTOSAVE_DEBOUNCE_MS,
        maxWaitMs: options?.maxWaitMs ?? AUTOSAVE_MAX_WAIT_MS,
        retryMs: options?.retryMs ?? AUTOSAVE_RETRY_MS,
    };
    const delayMs = (
        at: number,
        firstDirtyAt: number | null,
        lastChangeAt: number | null
    ) =>
        nextFlushDelayMs({
            now: at,
            firstDirtyAt,
            lastChangeAt,
            idleMs: policy.idleMs,
            maxWaitMs: policy.maxWaitMs,
        });
    const fire = schedule({ ...policy, delayMs });

    const state = {
        pending: null as Uint8Array | null,
        inFlight: null as Uint8Array | null,
        failed: false,
        firstDirtyAt: null as number | null,
        lastChangeAt: null as number | null,
        requestCount: 0,
    };
    let flushTimer: number | null = null;
    let retryTimer: number | null = null;

    const clearFlushTimer = () => {
        if (flushTimer !== null) {
            timers.clearTimeout(flushTimer);
            flushTimer = null;
        }
    };
    const clearRetryTimer = () => {
        if (retryTimer !== null) {
            timers.clearTimeout(retryTimer);
            retryTimer = null;
        }
    };

    /**
     * Arm the single flush timer with the earliest active deadline. The
     * idle debounce and the absolute max-wait deadline are collapsed by
     * `nextFlushDelayMs`; an armed retry backoff makes the flush due
     * immediately.
     */
    const armFlushTimer = (at: number) => {
        if (state.inFlight || !state.pending) return;
        const retryDue = retryTimer !== null;
        const policyDelay = retryDue
            ? 0
            : delayMs(at, state.firstDirtyAt, state.lastChangeAt);
        const delay = Math.max(0, policyDelay);
        clearFlushTimer();
        flushTimer = timers.setTimeout(() => {
            flushTimer = null;
            const reason: AutosaveFlushReason = retryDue
                ? 'retry'
                : policyDelay <= 0
                    ? 'idle'
                    : policyDelay < policy.idleMs
                        ? 'max-wait'
                        : 'idle';
            // The retry backoff was consumed by this flush.
            if (retryDue) clearRetryTimer();
            fire(reason, delay);
        }, delay);
    };

    return {
        get pending() {
            return state.pending;
        },
        get inFlight() {
            return state.inFlight !== null;
        },
        get failed() {
            return state.failed;
        },
        get requestCount() {
            return state.requestCount;
        },
        noteChange(bytes, at) {
            state.pending = bytes;
            if (state.firstDirtyAt === null) state.firstDirtyAt = at;
            state.lastChangeAt = at;
            state.failed = false; // a fresh edit supersedes a previous failure
            clearRetryTimer(); // real work wins over a scheduled retry
            if (state.inFlight) return; // the settle handler picks these up
            armFlushTimer(at);
        },
        beginRequest(at) {
            if (state.inFlight || !state.pending) return null;
            const payload = state.pending;
            state.inFlight = payload;
            state.pending = null;
            state.failed = false;
            state.requestCount += 1;
            clearFlushTimer();
            clearRetryTimer();
            void at;
            return payload;
        },
        settleRequest(ok, at) {
            // R26 repair: capture the payload BEFORE clearing `inFlight`.
            // The failure path used to read `state.inFlight` after it had
            // been nulled, so a failed upload was never restored: the
            // edits were dropped and no retry was scheduled.
            const uploaded = state.inFlight;
            if (!uploaded) return false;
            state.inFlight = null;
            if (!ok) {
                // The uploaded payload was the only copy of those edits and
                // the request failed: put it back on the dirty pile so the
                // next edit/flush retries it, and schedule one backoff.
                state.pending = state.pending ?? uploaded;
                state.failed = state.pending !== null;
                clearRetryTimer();
                if (state.failed) {
                    retryTimer = timers.setTimeout(() => {
                        retryTimer = null;
                        fire('retry', policy.retryMs);
                    }, policy.retryMs);
                }
                return false;
            }
            state.failed = false;
            if (state.pending) {
                // Newest bytes arrived while the request was in flight:
                // flush them now (still a single request at a time).
                state.firstDirtyAt = state.firstDirtyAt ?? at;
                state.lastChangeAt = state.lastChangeAt ?? at;
                return true;
            }
            // Fully idle: the next change starts a fresh idle/max-wait cycle.
            state.firstDirtyAt = null;
            state.lastChangeAt = null;
            return false;
        },
        cancelTimers() {
            clearFlushTimer();
            clearRetryTimer();
        },
        reset() {
            clearFlushTimer();
            clearRetryTimer();
            state.pending = null;
            state.inFlight = null;
            state.failed = false;
            state.firstDirtyAt = null;
            state.lastChangeAt = null;
        },
    };
}


// ============================================
// COMPONENT
// ============================================

export function DocumentView({
    projectId,
    activeView,
    activeUser,
    canEdit,
    aiProposal,
    onDismissAiProposal,
    onOpenTask,
    onSelectView,
}: DocumentViewProps) {
    const { language } = useI18n();
    const { t } = useDocumentI18n();
    const { toast } = useToast();

    // ------------------------------------------------------------------
    // Active tab. R20 (Bug 1 fix): the active view now comes from
    // the parent (project-detail.tsx) via the `activeView` prop,
    // mirroring Kanban/Calendar/Gantt. `null` = the synthetic
    // "General" tab. The previous internal `activeViewId` state is
    // gone — the prop IS the source of truth so the saved-views
    // sidebar selection propagates immediately to the editor.
    // ------------------------------------------------------------------
    const activeViewId = activeView?.id ?? null;

    const { data: viewsData, isLoading: viewsLoading } = useDocumentViews(projectId);
    const views = useMemo<SavedView[]>(
        () => (viewsData?.views ?? []).filter((v) => v.type === 'DOCUMENT'),
        [viewsData]
    );

    // ------------------------------------------------------------------
    // Mutations (create + delete)
    // ------------------------------------------------------------------
    const createView = useCreateSavedView(projectId);

    const handleCreate = useCallback(async () => {
        if (!canEdit) return;
        try {
            const created = await createView.mutateAsync({
                name: 'Untitled',
                isDefault: false,
                isShared: false,
                addToFavorites: false,
                type: 'DOCUMENT',
                config: buildDefaultConfig() as never,
            });
            // R24 (Bug — Document "General" entry): open the freshly
            // created document immediately. The parent owns
            // `activeViewId` (there is no local selection state), so we
            // ask it to select the new view via `onSelectView` instead
            // of leaving the user on the empty state. The parent's
            // auto-select effect would otherwise land on the first
            // DOCUMENT view — same result when this was the only one,
            // but explicit selection is what the user expects.
            onSelectView?.(created.id);
        } catch (err) {
            toast({
                title: 'Failed to create document',
                description: String(err),
                variant: 'destructive',
            });
        }
    }, [canEdit, createView, toast, onSelectView]);

    // ------------------------------------------------------------------
    // Yjs + WS connection for the active tab.
    // ------------------------------------------------------------------
    // R23: only saved DOCUMENT views have an authorized server room.
    // General stays local-only; VIEWERs hydrate authorized snapshots without
    // opening a writable Yjs protocol connection. The server independently
    // enforces these decisions; canEdit here is only a UI optimization.
    const roomName = activeViewId ? `view:${activeViewId}` : GENERAL_ROOM;
    const yjs = useYjsConnection(activeViewId, {
        userName: activeUser.name,
        roomName,
        // VIEWERs read authorized snapshots only; General is a local scratchpad.
        disabled: !activeViewId || !canEdit,
    });

    // ------------------------------------------------------------------
    // R14/R16: project task + member lists for the @-mention picker.
    //
    // R25 (Bug A): these lists are now only a FALLBACK / first paint for
    // the picker. The picker itself runs a debounced SERVER-SIDE search
    // (`/projects/:projectId/tasks?search=…&limit=50&offset=0`) so ANY
    // task in the project can be mentioned. We therefore no longer
    // truncate the list to 30 here — that pre-filter made tasks beyond
    // the first page unreachable.
    // ------------------------------------------------------------------
    const mentionTasksQuery = useTasks(projectId);
    const mentionTasks = useMemo(
        () => mentionTasksQuery.data?.tasks ?? [],
        [mentionTasksQuery.data]
    );

    // R16: project members appear in the same @-mention picker under
    // a "People" section. We use the existing useProjectMembers hook
    // (no filters) — people keep the R16 local-filter behaviour.
    const mentionMembersQuery = useProjectMembers(projectId);
    const mentionMembers = useMemo(
        () => mentionMembersQuery.data ?? [],
        [mentionMembersQuery.data]
    );

    // Mention click → delegate to the parent (project-detail) which
    // owns the task-detail side-panel.
    const handleMentionClick = useCallback(
        (taskId: string) => {
            if (!onOpenTask) return;
            onOpenTask(taskId);
        },
        [onOpenTask]
    );

    // ------------------------------------------------------------------
    // R25 (Bug B): person-mention click → popover with that person's
    // assigned tasks (each showing its status). The old "Mention sent"
    // toast is gone — it was comment-mode behaviour and nothing is sent
    // when you click a mention in a document.
    //
    // `anchorEl` is the chip that was clicked: Radix's `PopoverAnchor`
    // takes a virtual ref, so the popover can be anchored to a DOM node
    // that lives inside the ProseMirror node view.
    // ------------------------------------------------------------------
    const personAnchorRef = useRef<HTMLElement | null>(null);
    const [personTarget, setPersonTarget] =
        useState<DocumentPersonMentionTarget | null>(null);
    // Members arrive asynchronously; read them through a ref so the
    // click handler identity stays stable (it is part of the mention
    // extension's memo deps).
    const mentionMembersRef = useRef(mentionMembers);
    mentionMembersRef.current = mentionMembers;

    const handlePersonMentionClick = useCallback(
        (personId: string, displayName: string, anchor?: HTMLElement | null) => {
            const member = mentionMembersRef.current.find(
                (m) => m.user.id === personId
            );
            personAnchorRef.current = anchor ?? null;
            setPersonTarget({
                id: personId,
                label:
                    displayName ||
                    member?.user.name ||
                    member?.user.email ||
                    personId,
                avatarUrl: member?.user.avatarUrl ?? null,
                email: member?.user.email ?? null,
            });
        },
        []
    );

    const closePersonPopover = useCallback(() => {
        setPersonTarget(null);
    }, []);

    // ------------------------------------------------------------------
    // Snapshot hydrate + debounced auto-save.
    //
    // R26 — the "Saving… forever / only Ctrl+S saves" fix. The upload
    // policy now lives in `createAutosaveScheduler` above; this section
    // only wires it to React. Three rules keep `flushNow` IDENTITY-STABLE
    // (`useCallback(..., [applySaveStatus])` with a stable setter), which
    // is what makes the unmount/tab-switch cleanup effect below run on a
    // REAL unmount instead of on every render. The old bug: that effect
    // depended on a callback that depended on the `useMutation()` result
    // object — a fresh object every render — so its cleanup cancelled the
    // pending debounce timer and POSTed the whole document per keystroke.
    //
    //   • the view id is read from `activeViewIdRef`, never captured;
    //   • `saveMutation.mutate` is read from `saveRef`, refreshed each
    //     render, so a stale mutation closure can never be used;
    //   • `toast` is read from `toastRef` for the same reason.
    // ------------------------------------------------------------------
    const snapshotQuery = useDocumentSnapshot(activeViewId);
    const saveMutation = useSaveDocumentSnapshot(activeViewId);

    // R26: kept in STATE (not a ref) so the toolbar's "saved X ago" label
    // re-renders the moment a POST succeeds — with a ref the relative time
    // could stay stale until some unrelated re-render.
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

    const activeViewIdRef = useRef<string | null>(activeViewId);
    activeViewIdRef.current = activeViewId;
    const canEditRef = useRef(canEdit);
    canEditRef.current = canEdit;
    const saveRef = useRef(saveMutation.mutate);
    saveRef.current = saveMutation.mutate;
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const [saveStatus, setSaveStatus] = useState<DocumentSaveStatus>('saved');
    const saveStatusRef = useRef<DocumentSaveStatus>('saved');
    /** Same-document guard: a settled request only reports if it is still current. */
    const documentTokenRef = useRef(0);
    /**
     * One scheduler per mounted DocumentView. `visibleRef` pauses the
     * uploads while the tab is in the background — we flush on the way
     * out (visibilitychange → hidden, window blur) instead of chatting
     * to the API from a hidden tab.
     */
    const schedulerRef = useRef<AutosaveScheduler | null>(null);
    const visibleRef = useRef<boolean>(
        typeof document === 'undefined' || document.visibilityState !== 'hidden'
    );
    if (!schedulerRef.current) {
        schedulerRef.current = createAutosaveScheduler((policy) => (reason, delay) => {
            // The scheduler's own timer `delay` is 0 for a coalesced/retry
            // flush, so it must never gate the decision — `nextFlushDelayMs`
            // is the only source of truth for both deadline and reason.
            void policy;
            void delay;
            if (!visibleRef.current) return; // hidden tab: flushed on hide
            flushNowRef.current(reason);
        });
    }

    const applySaveStatus = useCallback((next: DocumentSaveStatus) => {
        if (saveStatusRef.current === next) return; // no redundant renders
        saveStatusRef.current = next;
        setSaveStatus(next);
    }, []);

    /**
     * Upload the pending bytes if — and only if — no request is in
     * flight. `beginRequest` refuses a second concurrent POST, so the
     * newest bytes simply stay pending and are flushed from the settle
     * handler of the request that is already running.
     */
    const flushNow = useCallback(
        (reason: AutosaveFlushReason) => {
            void reason;
            const scheduler = schedulerRef.current;
            if (!scheduler) return;
            const viewId = activeViewIdRef.current;
            if (!viewId) return;
            // `beginRequest` hands back the payload it just claimed (and
            // returns null when a request is already in flight or nothing
            // is pending).
            const bytes = scheduler.beginRequest(Date.now());
            if (!bytes) return;
            const token = documentTokenRef.current;
            const wasFailed = saveStatusRef.current === 'error';
            applySaveStatus('saving');

            // Guarded by `documentTokenRef`: settling a request that belongs
            // to a document we already left must not touch the current state.
            const stillCurrent = () => documentTokenRef.current === token;
            const settle = (ok: boolean) => {
                const retry = scheduler.settleRequest(ok, Date.now());
                if (retry) {
                    // Newest bytes arrived while this POST was in flight —
                    // upload them now (still at most one request at a time).
                    flushNowRef.current('idle');
                    return;
                }
                if (scheduler.failed && scheduler.pending) {
                    if (stillCurrent()) applySaveStatus('error');
                    // Exactly ONE toast per transition into the error state —
                    // repeated failures only update the status.
                    if (!wasFailed && stillCurrent()) {
                        toastRef.current({
                            title: tRef.current('document.saveStatus.error'),
                            description: tRef.current('document.saveStatus.errorHint'),
                            variant: 'destructive',
                        });
                    }
                    return;
                }
                if (stillCurrent()) applySaveStatus('saved');
            };

            saveRef.current(bytes, {
                onSuccess: (result) => {
                    setLastSavedAt(result.savedAt);
                },
                onSettled: (_result, error) => settle(!error),
            });
        },
        [applySaveStatus]
    );

    // Late-bound self reference: the scheduler callback (created once) and
    // the settle handler both re-enter `flushNow` without making it change
    // identity.
    const flushNowRef = useRef(flushNow);
    flushNowRef.current = flushNow;
    const tRef = useRef(t);
    tRef.current = t;

    const handleSnapshotChange = useCallback(
        (bytes: Uint8Array) => {
            // Read live values from refs so this callback — and therefore
            // the scheduler wiring — keeps a stable identity across renders.
            const viewId = activeViewIdRef.current;
            if (!viewId) return;
            // R20 (t62): VIEWERs are read-only — short-circuit the
            // upload so even if the underlying TipTap editor is
            // somehow editable (race condition), we never fire a
            // POST that the backend would reject with 403.
            if (!canEditRef.current) return;
            const scheduler = schedulerRef.current;
            if (!scheduler) return;
            // R26 repair: this used to bail out while
            // `pending === null && requestCount === 0` to "ignore the
            // hydration update". That condition also matches the FIRST
            // real keystroke, and since nothing is ever noted the state
            // never advances — so no document was ever autosaved at all.
            // Hydration/remote updates are now filtered at the source
            // (`isChangeOrigin` in document-editor.tsx), so every change
            // that reaches this callback is a local edit worth uploading.
            scheduler.noteChange(bytes, Date.now());
            // `saving` wins if the flush happened synchronously; an edit
            // during an in-flight POST only marks the bytes dirty.
            if (!scheduler.inFlight) applySaveStatus('dirty');
        },
        [applySaveStatus]
    );

    // ------------------------------------------------------------------
    // Flush on the way out — a REAL unmount or tab switch.
    //
    // IDENTITY FIX (R26, the core bug): the dependency list is EMPTY, so
    // this effect mounts once and its cleanup runs exactly once — on
    // unmount. The view id is read live from `activeViewIdRef` at cleanup
    // time, and the view-change effect below starts the incoming document
    // from a fresh scheduler. No `beforeunload`: a fetch cannot be awaited
    // there.
    // ------------------------------------------------------------------
    useEffect(() => {
        return () => {
            const scheduler = schedulerRef.current;
            if (!scheduler) return;
            scheduler.cancelTimers();
            // In-flight request: the bytes it carries are already on the
            // wire. Otherwise flush whatever was still pending.
            if (!scheduler.inFlight && scheduler.pending) flushNowRef.current('idle');
        };
    }, []);

    // Flush when the page stops being visible / loses focus so the last
    // few seconds of edits are not lost. This is also where uploads are
    // re-enabled after a background spell.
    useEffect(() => {
        const handleHide = () => {
            visibleRef.current = false;
            const scheduler = schedulerRef.current;
            if (!scheduler) return;
            scheduler.cancelTimers();
            if (!scheduler.inFlight && scheduler.pending) flushNowRef.current('idle');
        };
        const handleBlur = () => {
            const scheduler = schedulerRef.current;
            if (!scheduler) return;
            // A blur can happen while the page is still visible (focus
            // moved to devtools / another window): flush now.
            if (!scheduler.inFlight && scheduler.pending) flushNowRef.current('idle');
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') handleHide();
            else visibleRef.current = true;
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur', handleBlur);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    // ------------------------------------------------------------------
    // Document switch: flush the outgoing document and start the incoming
    // one from a clean slate, so one view's pending bytes can never be
    // uploaded under another view's id.
    // ------------------------------------------------------------------
    useEffect(() => {
        documentTokenRef.current += 1;
        const scheduler = schedulerRef.current;
        if (scheduler) {
            // A request still in flight belongs to the OLD token, so its
            // settle handler is ignored from here on.
            const stale = scheduler.inFlight;
            scheduler.cancelTimers();
            scheduler.reset();
            if (!stale) applySaveStatus('saved');
        }
        setLastSavedAt(null);
    }, [activeViewId, applySaveStatus]);


    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    // Empty state: no DOCUMENT views at all — show the friendly
    // "create your first document" CTA (creating one selects it via
    // `onSelectView`).
    const showEmptyState = !viewsLoading && views.length === 0 && activeViewId === null;
    // R24: `activeViewId === null` is NOT a writable scratchpad
    // anymore. When DOCUMENT views exist (or the list is still
    // loading) this is the transient "the parent's auto-select hasn't
    // landed yet" state — rendering the local-only editor here would
    // let the user type into a document that silently never saves, so
    // we show a neutral placeholder until a real view is selected.
    const showPendingSelection = activeViewId === null && !showEmptyState;

    return (
        <div
            className="flex flex-col gap-2 sm:gap-3"
            data-testid="document-view"
            data-active-view={activeViewId ?? 'none'}
        >
            {showEmptyState ? (
                <DocumentEmptyState canEdit={canEdit} onCreate={handleCreate} isCreating={createView.isPending} />
            ) : showPendingSelection ? (
                <div
                    className="flex items-center justify-center gap-2 rounded-md border border-dashed border-white/10 bg-surface/30 px-6 py-16 text-sm text-foreground-muted"
                    data-testid="document-view-pending-selection"
                >
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('document.loading')}
                </div>
            ) : (
                <>
                    {/* R20 (t62): VIEWERs see an explicit "Read-only"
                        badge above the toolbar so the role is visible
                        at a glance — not hidden in a network panel. */}
                    {!canEdit && (
                        <div className="flex items-center justify-end">
                            <span
                                data-testid="document-readonly-badge"
                                className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400"
                            >
                                <Eye className="h-3 w-3" />
                                {t('document.readOnlyBadge')}
                            </span>
                        </div>
                    )}
                    <DocumentToolbar
                        readOnly={!canEdit}
                        roomName={roomName}
                        connectionStatus={yjs.status}
                        peers={yjs.peers}
                        lastSavedAt={lastSavedAt}
                        // R26: the real save state machine ('saved' | 'dirty'
                        // | 'saving' | 'error') replaces the old
                        // `isSaving={saveMutation.isPending}` — the toolbar
                        // can now say "Unsaved changes" / "Save failed"
                        // instead of a permanent "Saved".
                        saveStatus={saveStatus}
                        connectionError={yjs.lastError ?? null}
                        // R18 (t59): download dropdown support.
                        viewName={
                            activeViewId
                                ? views.find((v) => v.id === activeViewId)?.name ?? t('document.defaultTabName')
                                : t('document.defaultTabName')
                        }
                        mentionTasks={mentionTasks as unknown as ReadonlyArray<{ id: string; title: string }>}
                        mentionMembers={
                            mentionMembers as unknown as ReadonlyArray<{ user: { id: string; name: string } }>
                        }
                    />
                    {aiProposal?.viewId === activeViewId && <div className="m-4 p-4 border border-primary/30 rounded-lg bg-primary/5 space-y-3">
                        <h3 className="font-semibold">{t('common.edit')} · AI</h3>
                        <p className="text-sm text-foreground/70">{String(aiProposal.explanation ?? '')}</p>
                        <div className="grid sm:grid-cols-2 gap-3 text-sm"><div><p className="mb-1 font-medium">{language === 'fr' ? 'Avant' : 'Before'}</p><pre className="whitespace-pre-wrap rounded bg-destructive/5 p-3 max-h-72 overflow-auto">{String(aiProposal.before ?? '')}</pre></div><div><p className="mb-1 font-medium">{language === 'fr' ? 'Proposition' : 'Proposed edit'}</p><pre className="whitespace-pre-wrap rounded bg-green-500/5 p-3 max-h-72 overflow-auto">{String(aiProposal.proposedText ?? '')}</pre></div></div>
                        <div className="flex gap-2"><Button disabled={!canEdit} onClick={async () => {
                            const editor = getDocumentEditor(roomName);
                            if (!editor) return;
                            try {
                                const current = await apiGet<{ version: number }>(`/ai/projects/${projectId}/documents/${activeViewId}/version`);
                                if (current.version !== aiProposal.expectedVersion) throw new Error(language === 'fr' ? 'Le document a changé. Préparez une nouvelle proposition.' : 'The document changed. Prepare a new proposal.');
                            } catch (error) { toast({ title: t('common.error'), description: error instanceof Error ? error.message : 'Edit failed', variant: 'destructive' }); return; }
                            try { applyDocumentTextEdits(editor, (aiProposal.edits ?? []) as DocumentTextEdit[], aiProposal as Parameters<typeof applyDocumentTextEdits>[2]); }
                            catch (error) { toast({ title: t('common.error'), description: error instanceof Error ? error.message : 'Edit failed', variant: 'destructive' }); return; }
                            onDismissAiProposal?.();
                        }}>{language === 'fr' ? 'Appliquer' : 'Apply'}</Button><Button variant="outline" onClick={onDismissAiProposal}>{t('common.cancel')}</Button></div>
                    </div>}
                    <DocumentEditor
                        roomName={roomName}
                        // R25 (Bug A): the picker needs the project id to
                        // run its own server-side task search.
                        projectId={projectId}
                        doc={yjs.doc}
                        provider={yjs.provider}
                        user={{ name: activeUser.name, color: peerColor(activeUser.id || 'anon') }}
                        snapshot={activeViewId ? (snapshotQuery.data ?? null) : null}
                        mentionTasks={mentionTasks}
                        mentionMembers={mentionMembers}
                        mentionLoading={mentionTasksQuery.isLoading || mentionMembersQuery.isLoading}
                        onMentionClick={handleMentionClick}
                        onPersonMentionClick={handlePersonMentionClick}
                        onSnapshotChange={handleSnapshotChange}
                        // R20 (t62): TipTap's editable flag drives the
                        // read-only state of the document body.
                        editable={canEdit}
                    />
                    {/* R25 (Bug B): person-mention popover — the tasks
                        assigned to the clicked person, each with its
                        status. Clicking a row opens the task detail
                        panel through `onOpenTask` (owned by the page).
                        Rendered here (not inside the editor) so it has
                        the project id, the query client and the
                        navigation callback. */}
                    <DocumentPersonMentionPopover
                        projectId={projectId}
                        target={personTarget}
                        anchorRef={personAnchorRef}
                        open={personTarget !== null}
                        onOpenChange={(next) => {
                            if (!next) closePersonPopover();
                        }}
                        onOpenTask={onOpenTask}
                    />
                </>
            )}
        </div>
    );
}

// ============================================
// HELPERS
// ============================================

function buildDefaultConfig(): ViewConfig {
    return {
        filters: {},
        lastSavedAt: null,
        snapshotBytes: 0,
    };
}

function peerColor(seed: string): string {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    const colors = [
        '#f87171', '#fb923c', '#fbbf24', '#a3e635', '#34d399',
        '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#fb7185',
    ] as const;
    return colors[Math.abs(hash) % colors.length] || colors[0];
}

// ============================================
// EMPTY STATE
// ============================================

function DocumentEmptyState({
    canEdit,
    onCreate,
    isCreating,
}: {
    canEdit: boolean;
    onCreate: () => void;
    isCreating: boolean;
}) {
    const { t } = useDocumentI18n();
    return (
        <div
            className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed border-white/10 bg-surface/30 px-6 py-16 text-center"
            data-testid="document-empty-state"
        >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-hover">
                <FileText className="h-8 w-8 text-foreground-muted" />
            </div>
            <h3 className="text-lg font-medium text-foreground">{t('document.emptyTitle')}</h3>
            <p className="max-w-sm text-sm text-foreground-muted">{t('document.emptyBody')}</p>
            {canEdit && (
                <Button
                    onClick={onCreate}
                    disabled={isCreating}
                    data-testid="document-empty-create-cta"
                >
                    {isCreating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <FileText className="mr-2 h-4 w-4" />
                    )}
                    {t('document.emptyCreateCta')}
                </Button>
            )}
        </div>
    );
}
